<script setup lang="tsx">
import type { MethodForm } from '@/types/module'
import { useI18n } from 'vue-i18n'
import { isRowClick } from '@/services/utils'
import PlusButton from '../widgets/PlusButton.vue'

const props = defineProps<{
	isEdit: boolean
}>()
const model = defineModel<MethodForm[]>({ required: true })

const { t } = useI18n()

function toggle(e: MouseEvent, i: number) {
	if (!isRowClick(e)) return

	model.value[i].isExpanded = !model.value[i].isExpanded
}
</script>

<template>
	<div class="table-responsive w-100">
		<table data-test="module-methods-table" class="table">
			<thead>
				<tr>
					<th class="w-auto-32"></th>
					<th class="col-2">{{ t('method.reference') }}</th>
					<th class="col-8">{{ t('description') }}</th>
					<th v-one-tooltip="t('securityNeed.label')">
						<i class="bi bi-shield-exclamation"></i>
					</th>
					<th class="">{{ t('method.force') }}</th>
				</tr>
			</thead>
			<tbody>
				<template v-for="(method, i) in model" :key="method.name">
					<tr @click.stop="toggle($event, i)" class="method-row" data-test="method-row" :data-test-id="method.id">
						<td><PlusButton v-model="model[i].isExpanded" /></td>
						<td data-test="method-name">{{ method.name }}</td>
						<td>{{ method.description }}</td>
						<td :class="method.applicable ? 'applicable' : ''">{{ method.type && t(method.type) }}</td>
						<td>
							<template v-if="props.isEdit">
								<div class="form-check form-switch align-self-center ms-auto">
									<input
										v-model="method.status.force"
										class="form-check-input"
										type="checkbox"
										role="switch"
										id="module-form-active"
										data-test="module-form-active"
									/>
								</div>
							</template>
							<template v-else>
								<i v-if="method.status.force" v-one-tooltip="t('misc.yes')" class="bi bi-circle-fill status-normal-background"></i>
								<i v-else v-one-tooltip="t('misc.no')" class="bi bi-circle-fill status-verybig-background"></i>
							</template>
						</td>
					</tr>
					<tr v-if="method.isExpanded" data-test="method-rules">
						<td colspan="4" class="p-3">
							<ul class="list-unstyled">
								<li v-for="(rule, i) in method.rules" :key="rule.id" class="mb-3" data-test="rule" :data-test-id="rule.id">
									<p class="mb-0 text-muted">{{ rule.value }}</p>
									<ul class="mb-2">
										<li v-for="subrule in rule.subrules" :key="subrule.id" class="text-muted">{{ subrule.value }}</li>
									</ul>
									<hr v-if="i < method.rules.length - 1" />
								</li>
							</ul>
						</td>
					</tr>
				</template>
			</tbody>
		</table>
	</div>
</template>

<style lang="sass" scoped>
.applicable
	color: #62d837
	font-weight: bold
</style>
